<template>
  <div
    class="side-menu"
    :data-testid="dataTestId.sidebar.sideMenu"
  >
    <picture class="side-menu-header">
      <source
        type="image/webp"
        srcset="@/assets/img/expirenza-logo.webp 1x, @/assets/img/expirenza-logo@2x.webp 2x"
      />
      <source
        type="image/png"
        srcset="@/assets/img/expirenza-logo.png 1x, @/assets/img/expirenza-logo@2x.png 2x"
      />
      <img
        :data-testid="dataTestId.sidebar.logo"
        src="@/assets/img/expirenza-logo.png"
        srcset="@/assets/img/expirenza-logo.png 1x, @/assets/img/expirenza-logo@2x.png 2x"
        loading="lazy"
        alt="expirenza"
      />
    </picture>
    <div
      v-if="route.fullPath !== '/'"
      class="side-menu-body"
    >
      <RouterLink
        v-if="restaurant.title"
        :data-testid="dataTestId.sidebar.restaurantButton"
        v-slot="{href, route, navigate, isActive, isExactActive}"
        to="/"
        class="body-text-s medium"
        custom
      >
        <div
          class="restaurant-slot"
          @click="navigate"
        >
          <img
            v-if="restaurant.logoURL"
            :src="restaurant.logoURL"
            :alt="restaurant.title"
          />
          <p class="body-text-s strong">{{ restaurant.title }}</p>
          <BaseIcon
            icon="arrow-right-light"
            class="icon"
          />
        </div>
      </RouterLink>
      <nav
        v-if="isLoggedIn && isMenuSectionsVisible"
        class="navigation-slot"
      >
        <RouterLink
          :data-testid="dataTestId.sidebar.dashboardButton"
          :to="{
            name: 'dashboard',
            query: {id: restaurant.id},
          }"
          activeClass="active-route"
          class="body-text-s medium"
        >
          <BaseIcon icon="house" />{{ $t("sidebar.dashboard") }}</RouterLink
        >
        <RouterLink
          v-if="isGuestDatabaseVisible"
          :data-testid="dataTestId.sidebar.guestBaseButton"
          :to="{
            name: 'profile-list',
            query: {restaurant_id: restaurant.expirenzaUuid ?? restaurant.id},
          }"
          activeClass="active-route"
          class="body-text-s medium"
        >
          <BaseIcon icon="people" />{{ $t("sidebar.guestList") }}</RouterLink
        >
        <RouterLink
          v-if="isGuestOffersVisible"
          :data-testid="dataTestId.sidebar.offersButton"
          :to="{
            name: 'offers',
            query: {id: restaurant.expirenzaUuid ?? restaurant.id},
          }"
          activeClass="active-route"
          class="body-text-s medium"
        >
          <BaseIcon icon="card" />{{ $t("sidebar.offers") }}</RouterLink
        >
        <RouterLink
          v-if="isDigestVisible"
          :to="{
            name: 'digest',
            query: {id: restaurant.id},
          }"
          activeClass="active-route"
          class="body-text-s medium"
        >
          <BaseIcon icon="receipt" />{{ $t("sidebar.digest") }}</RouterLink
        >
        <RouterLink
          v-if="isAiAssistantVisible"
          :to="{name: 'ai-assistant'}"
          activeClass="active-route"
          class="body-text-s medium"
        >
          <BaseIcon icon="rocket" />{{ $t("sidebar.aiAssistant") }}</RouterLink
        >
        <RouterLink
          v-if="false"
          to="/ui-kit"
          activeClass="active-route"
        >
          <BaseIcon icon="edit" />
          UI kit
        </RouterLink>
      </nav>
    </div>
    <div class="side-menu-footer">
      <div
        class="auth-button"
        :data-testid="dataTestId.sidebar.logOutButton"
        @click="handleClick"
      >
        <p class="body-text-s strong">
          {{ isLoggedIn ? getUserInfo.phone : $t("auth.loginButton") }}
        </p>
        <img
          src="@/assets/icons/sign-out.svg"
          alt="burder"
          width="24"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useRouter, useRoute} from "vue-router"
import {RoutesEnum} from "@/router/types"
import {useRestaurantStore} from "@/stores/restaurant"
import {useAuthStore} from "@/stores/auth"
import BaseIcon from "@/components/BaseIcon.vue"
import {computed} from "vue"
import {UserRoles} from "@/stores/auth/types"
import dataTestId from "@/constants/dataTestId"

const restaurantStore = useRestaurantStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

const {restaurantSideBarInfo: restaurant} = storeToRefs(restaurantStore)
const {getUserInfo, isLoggedIn, currentRestaurantOptions} = storeToRefs(authStore)

function handleClick() {
  if (isLoggedIn.value) {
    authStore.logout()
  } else {
    router.push(RoutesEnum.UserLogin)
  }
}
const isMenuSectionsVisible = computed(() => {
  return authStore.roleAccessGuard([UserRoles.adminEditor, UserRoles.guestAnalytics, UserRoles.aidaAnalytics])
})

const digestRestaurantIds = (import.meta.env.VITE_QUICK_REPORT_REST_ID ?? "")
  .split(",")
  .map((id: string) => id.trim())
  .filter(Boolean)

const isDigestVisible = computed(() =>
  !!restaurant.value.id && digestRestaurantIds.includes(restaurant.value.id)
)

const isAiAssistantVisible = computed(() =>
  authStore.roleAccessGuard([UserRoles.aidaAnalytics]) && !!currentRestaurantOptions.value?.crm_aida_enabled
)

const isGuestDatabaseVisible = computed(() =>
  !!currentRestaurantOptions.value?.crm_guest_database_enabled
)

const isGuestOffersVisible = computed(() =>
  !!currentRestaurantOptions.value?.crm_offers_enabled
)
</script>
<style scoped lang="less">
.side-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;

  &-header {
    padding: 8px;

    img {
      width: 128px;
    }
  }

  &-body {
    margin-top: 24px;

    .restaurant-slot {
      display: flex;
      align-items: center;
      gap: 12px;
      border-radius: 12px;
      border: 1px solid var(--border-color-default);
      padding: 10px 8px;
      background-color: var(--static-white);
      cursor: pointer;
      @media (min-width: 1024px) {
        gap: 8px;
      }

      img {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .icon {
        color: var(--p-neutral-500);
        font-size: 16px;
        flex-shrink: 0;
        margin-left: auto;
      }
    }

    .navigation-slot {
      margin-top: 16px;
      display: flex;
      flex-direction: column;

      a {
        padding: 10px 8px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--fg-default);
        text-decoration: none;

        .base-icon {
          color: var(--fg-soft);
        }
      }
    }
  }

  &-footer {
    margin-top: auto;
    padding: 8px;
    border-top: 1px solid var(--border-color-default);

    span {
      color: var(--static-black);
    }

    .auth-button {
      color: var(--fg-default);
      margin-top: 8px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
  }
}

.side-menu-body .navigation-slot a.active-route {
  background: var(--p-neutral-200);
  font-weight: 600;

  .base-icon {
    color: var(--p-primary-600);
  }
}
</style>
